import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Layers, Package, BarChart3, Users, TrendingUp, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { language, t } = useLanguage();
  const [stats, setStats] = useState({
    total_services: 0,
    total_packages: 0,
    active_services: 0,
    active_packages: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: t('admin_total_services'),
      value: stats.total_services,
      icon: Layers,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      title: t('admin_active_services'),
      value: stats.active_services,
      icon: TrendingUp,
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50',
    },
    {
      title: t('admin_total_packages'),
      value: stats.total_packages,
      icon: Package,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      title: t('admin_active_packages'),
      value: stats.active_packages,
      icon: BarChart3,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('admin_welcome')}</h1>
        <p className="text-slate-500">
          {language === 'ar'
            ? 'إدارة محتوى موقع WatsFlow من هنا'
            : 'Manage WatsFlow website content from here'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="border-none shadow-sm hover:shadow-md transition-shadow" data-testid={`stat-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-900">
                        {loading ? '...' : stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <IconComponent className={`w-6 h-6 text-${stat.color.replace('bg-', '')}`} style={{ color: stat.color.includes('emerald') ? '#10b981' : stat.color.includes('blue') ? '#3b82f6' : stat.color.includes('purple') ? '#a855f7' : '#f97316' }} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'ar' ? 'إجراءات سريعة' : 'Quick Actions'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <a
              href="/admin/services"
              className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Layers className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">{t('admin_add_service')}</p>
                <p className="text-sm text-slate-500">
                  {language === 'ar' ? 'أضف خدمة جديدة للموقع' : 'Add new service to the website'}
                </p>
              </div>
            </a>
            <a
              href="/admin/packages"
              className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">{t('admin_add_package')}</p>
                <p className="text-sm text-slate-500">
                  {language === 'ar' ? 'أضف باقة جديدة للعملاء' : 'Add new package for customers'}
                </p>
              </div>
            </a>
          </CardContent>
        </Card>

        {/* Client Area Integration Info */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'ar' ? 'تكامل Client Area' : 'Client Area Integration'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-slate-600">
                {language === 'ar'
                  ? 'يمكنك ربط نظام Client Area الخاص بك عبر API endpoints التالية:'
                  : 'You can connect your Client Area system via the following API endpoints:'}
              </p>
              <div className="bg-slate-900 rounded-xl p-4 space-y-2 font-mono text-sm">
                <p className="text-emerald-400">GET /api/client-area/packages</p>
                <p className="text-slate-400">{language === 'ar' ? '← جلب الباقات المتاحة' : '← Get available packages'}</p>
                <p className="text-emerald-400 mt-2">GET /api/client-area/package/:id</p>
                <p className="text-slate-400">{language === 'ar' ? '← جلب تفاصيل باقة محددة' : '← Get specific package details'}</p>
                <p className="text-emerald-400 mt-2">POST /api/client-area/webhook</p>
                <p className="text-slate-400">{language === 'ar' ? '← استقبال أحداث من Client Area' : '← Receive events from Client Area'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
